/**
 * Domain Setup Tab Component
 * Manages custom domain and DNS configuration
 */

import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Globe,
  Server,
  Settings,
  CheckCircle,
  AlertCircle,
  Copy,
  Save,
  Eye,
  EyeOff,
  Cloud,
  Mail,
  RefreshCw,
  Plus,
  FileText,
  AlertTriangle,
  Info
} from 'lucide-react';
import { SuperAdminOnboardingData, getStatusBadgeVariant, getStatusDisplayName } from '@/types/super-admin-onboarding';
import { OnboardingStatus, SetupMethod } from '@/types/onboarding';
import { toast } from '@/hooks/use-toast';
import { useEmailDomain } from '@/hooks/useEmailDomain';

interface DomainSetupTabProps {
  data: SuperAdminOnboardingData;
  onFieldUpdate: (field: keyof SuperAdminOnboardingData, value: any) => Promise<void>;
  onBulkFieldUpdate?: (updates: Record<string, any>) => Promise<{ success: boolean; changedCount: number }>;
  onStatusUpdate: (status: OnboardingStatus) => Promise<void>;
}

const DomainSetupTab: React.FC<DomainSetupTabProps> = ({
  data,
  onFieldUpdate,
  onBulkFieldUpdate,
  onStatusUpdate
}) => {
  // Email domain hook for Cloudflare/SES management
  const {
    loading: domainLoading,
    fetchDomainByOrganisation,
    registerDomain,
    registerDomainManual,
    importExistingDomain,
    verifyNameservers,
    addSubdomainRecord,
    syncSESRecords,
    checkVerification,
    getDNSInstructions
  } = useEmailDomain();

  const [formData, setFormData] = useState({
    dns_setup_method: data.dns_setup_method || SetupMethod.REMOTE_ACCESS,
    root_domain: data.root_domain || '',
    custom_sub_domain: data.custom_sub_domain || '',
    review_management_subdomain: data.review_management_subdomain || '',
    ordering_subdomain_ip: data.ordering_subdomain_ip || '',
    review_management_ip: data.review_management_ip || '',
    dns_provider_name: data.dns_provider_name || '',
    dns_provider_custom_name: data.dns_provider_custom_name || '',
    dns_provider_username: data.dns_provider_username || '',
    dns_provider_password: data.dns_provider_password || '',
    nameserver_1: data.nameserver_1 || '',
    nameserver_2: data.nameserver_2 || ''
  });

  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [showPassword, setShowPassword] = useState(true); // Default to showing password
  const [emailDomain, setEmailDomain] = useState<any>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [dnsMode, setDnsMode] = useState<'cloudflare' | 'manual'>('cloudflare');
  const [showDnsInstructions, setShowDnsInstructions] = useState(false);
  const [dnsInstructions, setDnsInstructions] = useState<any>(null);

  // Fetch email domain record when component loads or domain changes
  useEffect(() => {
    if (formData.root_domain && data.organisation_id) {
      loadEmailDomain();
    }
  }, [formData.root_domain, data.organisation_id]);

  // Load email domain from database
  const loadEmailDomain = async () => {
    if (!formData.root_domain || !data.organisation_id) return;

    const domain = await fetchDomainByOrganisation(data.organisation_id, formData.root_domain);
    setEmailDomain(domain);
  };

  // Handle form field changes
  const handleFieldChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    setHasChanges(true);
  };

  // Save all changes using bulk update
  const handleSave = async () => {
    try {
      setSaving(true);
      
      if (onBulkFieldUpdate) {
        // Use the new bulk update function
        const result = await onBulkFieldUpdate(formData);
        
        if (result.changedCount === 0) {
          toast({
            title: "No Changes",
            description: "No fields were modified"
          });
        } else {
          toast({
            title: "Success",
            description: `Updated ${result.changedCount} field(s) successfully`
          });
        }
      } else {
        // Fallback to individual updates if bulk update not available
        const changedFields = Object.entries(formData).filter(([field, value]) => {
          const originalValue = data[field as keyof SuperAdminOnboardingData];
          return originalValue !== value;
        });
        
        if (changedFields.length === 0) {
          toast({
            title: "No Changes",
            description: "No fields were modified"
          });
          return;
        }
        
        const updates = changedFields.map(([field, value]) => 
          onFieldUpdate(field as keyof SuperAdminOnboardingData, value)
        );
        
        await Promise.all(updates);
        
        toast({
          title: "Success",
          description: `Updated ${changedFields.length} field(s) successfully`
        });
      }
      
      setHasChanges(false);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to save domain setup information",
        variant: "destructive"
      });
    } finally {
      setSaving(false);
    }
  };

  // Update status
  const handleStatusUpdate = async (status: OnboardingStatus) => {
    try {
      // Save changes first if any
      if (hasChanges) {
        await handleSave();
      }
      
      await onFieldUpdate('dns_status', status);
      
      toast({
        title: "Success",
        description: `Domain setup status updated to ${getStatusDisplayName(status)}`
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update status",
        variant: "destructive"
      });
    }
  };

  // Copy A Records instructions email
  const handleCopyARecordsEmail = async () => {
    const aRecordsEmail = `Hi there,

We need your help setting up DNS records for your Pump'd online ordering integration. Please add the following DNS records to your domain:

Domain: ${formData.root_domain}

DNS RECORDS TO ADD:

1. Ordering Page DNS Record (Required)
   Type: A
   Name: ${formData.custom_sub_domain}
   Value: ${formData.ordering_subdomain_ip}
   TTL: 1 min (or as low as possible)
   
   This will create: https://${formData.custom_sub_domain}.${formData.root_domain}

2. Review Management DNS Record (Required)
   Type: A  
   Name: ${formData.review_management_subdomain}
   Value: ${formData.review_management_ip}
   TTL: 1 min (or as low as possible)
   
   This will create: https://${formData.review_management_subdomain}.${formData.root_domain}

INSTRUCTIONS:

1. Log in to your domain provider/DNS management panel
2. Look for "DNS Management", "DNS Records", or "Name Servers" section
3. Add the A records listed above
4. Verify the records are active (this usually takes a few minutes)
5. Once records are active, please confirm completion

HELPFUL RESOURCES:
- Cloudflare DNS Guide: https://developers.cloudflare.com/dns/manage-dns-records/how-to/create-dns-records/
- GoDaddy DNS Guide: https://godaddy.com/help/add-an-a-record-19238

IMPORTANT: Please confirm once the DNS records are active. We can set up most of the domain configuration immediately, but the SSL certificate generation must wait until the DNS records have propagated.

Thanks for your help!

Best regards,
Your Pump'd Customer Success Team`.trim();

    try {
      await navigator.clipboard.writeText(aRecordsEmail);
      toast({
        title: "Copied",
        description: "A Records instructions email copied to clipboard"
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to copy A Records instructions",
        variant: "destructive"
      });
    }
  };

  // Copy Nameserver instructions email
  const handleCopyNameserverEmail = async () => {
    const nameserverEmail = `Hi,

I hope you're doing well. I need your assistance with updating the nameservers for your domain to complete your online ordering system setup.

Please make the following nameserver changes in your domain control panel:

Domain: ${formData.root_domain}

Replace the existing nameservers with these Cloudflare nameservers:
• Nameserver 1: ${formData.nameserver_1}
• Nameserver 2: ${formData.nameserver_2}

Steps to complete:
1. Log in to your domain provider (GoDaddy, Crazy Domains, Free Parking, etc.)
2. Navigate to the DNS Management or Name Servers section
3. Delete all existing nameservers
4. Add the two Cloudflare nameservers listed above
5. Save the changes

This change will allow Pump'd to manage your DNS settings through Cloudflare, which will enable your custom domain ordering system.

If you need any clarification or run into issues, please let me know.

Thank you!

Best regards,
Your Pump'd Customer Success Team`.trim();

    try {
      await navigator.clipboard.writeText(nameserverEmail);
      toast({
        title: "Copied",
        description: "Nameserver instructions email copied to clipboard"
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to copy nameserver instructions",
        variant: "destructive"
      });
    }
  };

  // Auto-generate subdomains
  const handleGenerateSubdomains = () => {
    if (!formData.root_domain) {
      toast({
        title: "Error",
        description: "Please enter a root domain first",
        variant: "destructive"
      });
      return;
    }

    setFormData(prev => ({
      ...prev,
      custom_sub_domain: `order`,
      review_management_subdomain: `reviews`
      // Note: IP addresses are NOT modified - only subdomain names are generated
    }));
    setHasChanges(true);

    toast({
      title: "Generated",
      description: "Default subdomains have been generated (order & reviews)"
    });
  };

  // === EMAIL DOMAIN MANAGEMENT HANDLERS ===

  // Refresh email domain data
  const handleRefreshDomain = async () => {
    setRefreshing(true);
    await loadEmailDomain();
    setRefreshing(false);
  };

  // Register domain with Cloudflare
  const handleRegisterWithCloudflare = async () => {
    if (!formData.root_domain) {
      toast({
        title: "Error",
        description: "Please enter a root domain first",
        variant: "destructive"
      });
      return;
    }

    if (!data.organisation_id) {
      toast({
        title: "Organisation ID Required",
        description: "Please complete the Initial Setup tab first to generate an organisation ID before registering the domain",
        variant: "destructive"
      });
      return;
    }

    // Save any pending changes first
    if (hasChanges) {
      await handleSave();
    }

    const result = await registerDomain({
      organisationId: data.organisation_id,
      domain: formData.root_domain,
      defaultFromEmail: `info@${formData.root_domain}`,
      defaultFromName: data.organisation_name || 'Restaurant',
      userOnboardingId: data.id  // Pass user_onboarding ID for nameserver sync
    });

    if (result.success && result.nameservers) {
      // Auto-update nameservers in user_onboarding table
      await onFieldUpdate('nameserver_1', result.nameservers[0]);
      await onFieldUpdate('nameserver_2', result.nameservers[1]);

      // Update local form data
      setFormData(prev => ({
        ...prev,
        nameserver_1: result.nameservers![0],
        nameserver_2: result.nameservers![1]
      }));

      // Reload domain data
      await loadEmailDomain();
    }
  };

  // Register domain with manual DNS (customer manages DNS)
  const handleRegisterManual = async () => {
    if (!formData.root_domain) {
      toast({
        title: "Error",
        description: "Please enter a root domain first",
        variant: "destructive"
      });
      return;
    }

    if (!data.organisation_id) {
      toast({
        title: "Organisation ID Required",
        description: "Please complete the Initial Setup tab first to generate an organisation ID before registering the domain",
        variant: "destructive"
      });
      return;
    }

    // Save any pending changes first
    if (hasChanges) {
      await handleSave();
    }

    const result = await registerDomainManual({
      organisationId: data.organisation_id,
      domain: formData.root_domain,
      defaultFromEmail: `info@${formData.root_domain}`,
      defaultFromName: data.organisation_name || 'Restaurant'
    });

    if (result.success) {
      // Show DNS instructions
      setDnsInstructions(result);
      setShowDnsInstructions(true);

      // Reload domain data
      await loadEmailDomain();
    }
  };

  // Import existing Cloudflare domain
  const handleImportExisting = async () => {
    if (!formData.root_domain) {
      toast({
        title: "Error",
        description: "Please enter a root domain first",
        variant: "destructive"
      });
      return;
    }

    if (!data.organisation_id) {
      toast({
        title: "Organisation ID Required",
        description: "Please complete the Initial Setup tab first to generate an organisation ID before importing the domain",
        variant: "destructive"
      });
      return;
    }

    // Save any pending changes first
    if (hasChanges) {
      await handleSave();
    }

    const result = await importExistingDomain({
      organisationId: data.organisation_id,
      domain: formData.root_domain,
      defaultFromEmail: `info@${formData.root_domain}`,
      defaultFromName: data.organisation_name || 'Restaurant'
    });

    if (result.success && result.nameservers) {
      // Auto-update nameservers in user_onboarding table
      await onFieldUpdate('nameserver_1', result.nameservers[0]);
      await onFieldUpdate('nameserver_2', result.nameservers[1]);

      // Update local form data
      setFormData(prev => ({
        ...prev,
        nameserver_1: result.nameservers![0],
        nameserver_2: result.nameservers![1]
      }));

      // Reload domain data
      await loadEmailDomain();
    }
  };

  // Verify nameservers
  const handleVerifyNameservers = async () => {
    if (!emailDomain) return;

    const result = await verifyNameservers(emailDomain.id);

    if (result.success) {
      // Reload domain data to show updated status
      await loadEmailDomain();
    }
  };

  // Add ordering subdomain A record
  const handleAddOrderingRecord = async () => {
    if (!emailDomain || !formData.custom_sub_domain || !formData.ordering_subdomain_ip) {
      toast({
        title: "Error",
        description: "Please configure ordering subdomain and IP address first",
        variant: "destructive"
      });
      return;
    }

    await addSubdomainRecord({
      domainId: emailDomain.id,
      subdomain: formData.custom_sub_domain,
      ipAddress: formData.ordering_subdomain_ip,
      proxied: false
    });
  };

  // Add reviews subdomain A record
  const handleAddReviewsRecord = async () => {
    if (!emailDomain || !formData.review_management_subdomain || !formData.review_management_ip) {
      toast({
        title: "Error",
        description: "Please configure reviews subdomain and IP address first",
        variant: "destructive"
      });
      return;
    }

    await addSubdomainRecord({
      domainId: emailDomain.id,
      subdomain: formData.review_management_subdomain,
      ipAddress: formData.review_management_ip,
      proxied: false
    });
  };

  // Sync SES DNS records to Cloudflare
  const handleSyncSESRecords = async () => {
    if (!emailDomain) return;

    const result = await syncSESRecords(emailDomain.id);

    if (result.success) {
      // Reload domain data to show updated status
      await loadEmailDomain();
    }
  };

  // Check AWS SES verification
  const handleCheckVerification = async () => {
    if (!emailDomain) return;

    const result = await checkVerification(emailDomain.id);

    if (result.success) {
      // Reload domain data to show updated status
      await loadEmailDomain();
    }
  };

  const isComplete = data.dns_status === OnboardingStatus.COMPLETE;
  const hasBasicDomainInfo = formData.root_domain && formData.custom_sub_domain;

  return (
    <div className="space-y-6">
      {/* Header with Status */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-primary/10 rounded-lg">
                <Globe className="h-5 w-5 text-primary" />
              </div>
              <div>
                <CardTitle>Domain Setup</CardTitle>
                <p className="text-sm text-muted-foreground">
                  Configure custom domain and DNS settings
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={handleRefreshDomain}
                disabled={refreshing || !formData.root_domain}
              >
                <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
              </Button>
              <Badge variant={getStatusBadgeVariant(data.dns_status || OnboardingStatus.NOT_STARTED)}>
                {getStatusDisplayName(data.dns_status || OnboardingStatus.NOT_STARTED)}
              </Badge>
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* Cloudflare & AWS SES Registration Status */}
      {formData.root_domain && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Cloud className="h-5 w-5" />
              Domain Registration Status
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {!emailDomain ? (
              <div className="p-4 bg-muted/50 rounded-lg space-y-3">
                <p className="text-sm text-muted-foreground">
                  Domain not registered in our system yet. Choose DNS management mode:
                </p>

                {/* DNS Mode Selector */}
                <div className="space-y-2">
                  <Label>DNS Management Mode</Label>
                  <Select value={dnsMode} onValueChange={(value: 'cloudflare' | 'manual') => setDnsMode(value)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="cloudflare">
                        <div className="flex items-center gap-2">
                          <Cloud className="h-4 w-4" />
                          <span>Cloudflare (Automatic DNS)</span>
                        </div>
                      </SelectItem>
                      <SelectItem value="manual">
                        <div className="flex items-center gap-2">
                          <Mail className="h-4 w-4" />
                          <span>Manual (Customer Manages DNS)</span>
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Cloudflare Mode Buttons */}
                {dnsMode === 'cloudflare' && (
                  <>
                    <div className="flex flex-col sm:flex-row gap-2">
                      <Button
                        onClick={handleRegisterWithCloudflare}
                        disabled={domainLoading}
                        className="flex-1"
                      >
                        <Cloud className="h-4 w-4 mr-2" />
                        Register New Domain
                      </Button>
                      <Button
                        onClick={handleImportExisting}
                        disabled={domainLoading}
                        variant="outline"
                        className="flex-1"
                      >
                        <Cloud className="h-4 w-4 mr-2" />
                        Import from Cloudflare
                      </Button>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      <strong>Register New:</strong> Create a new Cloudflare zone (use if domain is not in Cloudflare yet)<br />
                      <strong>Import Existing:</strong> Link an existing Cloudflare zone (use if domain already managed in Cloudflare)
                    </p>
                  </>
                )}

                {/* Manual Mode Button */}
                {dnsMode === 'manual' && (
                  <>
                    <Button
                      onClick={handleRegisterManual}
                      disabled={domainLoading}
                      className="w-full"
                    >
                      <Mail className="h-4 w-4 mr-2" />
                      Register for Manual DNS
                    </Button>
                    <div className="p-3 bg-blue-50 dark:bg-blue-950/50 rounded-lg border border-blue-200 dark:border-blue-800">
                      <p className="text-sm text-blue-800 dark:text-blue-200">
                        <strong>Manual DNS Mode:</strong> Domain will be registered with AWS SES only.
                        DNS instructions will be provided for the customer to add to their DNS provider.
                      </p>
                    </div>
                  </>
                )}
              </div>
            ) : (
              <div className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="p-4 bg-muted/50 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <Cloud className="h-4 w-4" />
                      <span className="font-medium">Cloudflare</span>
                    </div>
                    <div className="space-y-1 text-sm">
                      <div>Status: <Badge variant="outline">{emailDomain.cloudflare_zone_status || 'pending'}</Badge></div>
                      <div className="text-xs text-muted-foreground">Zone ID: {emailDomain.cloudflare_zone_id?.substring(0, 8)}...</div>
                    </div>
                    {emailDomain.cloudflare_zone_status !== 'active' && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="mt-3 w-full"
                        onClick={handleVerifyNameservers}
                        disabled={domainLoading}
                      >
                        <RefreshCw className="h-3 w-3 mr-2" />
                        Verify Nameservers
                      </Button>
                    )}
                  </div>

                  <div className="p-4 bg-muted/50 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <Mail className="h-4 w-4" />
                      <span className="font-medium">AWS SES</span>
                    </div>
                    <div className="space-y-1 text-sm">
                      <div>Verified: <Badge variant={emailDomain.is_verified ? "default" : "secondary"}>
                        {emailDomain.is_verified ? 'Yes' : 'No'}
                      </Badge></div>
                      <div>DNS Synced: <Badge variant={emailDomain.dns_records_synced ? "default" : "secondary"}>
                        {emailDomain.dns_records_synced ? 'Yes' : 'No'}
                      </Badge></div>
                    </div>
                    {!emailDomain.dns_records_synced && emailDomain.cloudflare_zone_status === 'active' && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="mt-3 w-full"
                        onClick={handleSyncSESRecords}
                        disabled={domainLoading}
                      >
                        <Mail className="h-3 w-3 mr-2" />
                        Sync SES DNS Records
                      </Button>
                    )}
                    {emailDomain.dns_records_synced && !emailDomain.is_verified && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="mt-3 w-full"
                        onClick={handleCheckVerification}
                        disabled={domainLoading}
                      >
                        <CheckCircle className="h-3 w-3 mr-2" />
                        Check Verification
                      </Button>
                    )}
                  </div>
                </div>

                {emailDomain.cloudflare_nameservers && (
                  <div className="p-4 bg-blue-50 dark:bg-blue-950/50 rounded-lg border border-blue-200 dark:border-blue-800">
                    <p className="text-sm font-medium mb-2">Nameservers (provide to customer):</p>
                    <div className="font-mono text-xs space-y-1">
                      {emailDomain.cloudflare_nameservers.map((ns: string, i: number) => (
                        <div key={i}>{ns}</div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* A Records Configuration */}
      {emailDomain && formData.custom_sub_domain && formData.ordering_subdomain_ip && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Server className="h-5 w-5" />
              Subdomain A Records
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="p-4 bg-muted/50 rounded-lg space-y-2">
                <div className="font-medium text-sm">Ordering Subdomain</div>
                <div className="text-xs font-mono">{formData.custom_sub_domain}.{formData.root_domain}</div>
                <div className="text-xs text-muted-foreground">→ {formData.ordering_subdomain_ip}</div>
                <Button
                  size="sm"
                  variant="outline"
                  className="w-full"
                  onClick={handleAddOrderingRecord}
                  disabled={domainLoading || emailDomain.cloudflare_zone_status !== 'active'}
                >
                  <Plus className="h-3 w-3 mr-2" />
                  Add A Record
                </Button>
              </div>

              {formData.review_management_subdomain && formData.review_management_ip && (
                <div className="p-4 bg-muted/50 rounded-lg space-y-2">
                  <div className="font-medium text-sm">Reviews Subdomain</div>
                  <div className="text-xs font-mono">{formData.review_management_subdomain}.{formData.root_domain}</div>
                  <div className="text-xs text-muted-foreground">→ {formData.review_management_ip}</div>
                  <Button
                    size="sm"
                    variant="outline"
                    className="w-full"
                    onClick={handleAddReviewsRecord}
                    disabled={domainLoading || emailDomain.cloudflare_zone_status !== 'active'}
                  >
                    <Plus className="h-3 w-3 mr-2" />
                    Add A Record
                  </Button>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Setup Method */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Settings className="h-5 w-5" />
            Setup Method
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label>DNS Setup Method</Label>
            <Select
              value={formData.dns_setup_method}
              onValueChange={(value) => handleFieldChange('dns_setup_method', value)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select setup method" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={SetupMethod.REMOTE_ACCESS}>Remote Access Setup</SelectItem>
                <SelectItem value={SetupMethod.DIY}>Customer Self-Setup</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {formData.dns_setup_method === SetupMethod.REMOTE_ACCESS ? (
            <div className="p-4 bg-blue-50 dark:bg-blue-950/50 rounded-lg border border-blue-200 dark:border-blue-800">
              <p className="text-sm text-blue-800 dark:text-blue-200">
                <strong>Remote Access Setup:</strong> Our team will handle the DNS configuration with the customer's provider credentials.
              </p>
            </div>
          ) : (
            <div className="p-4 bg-green-50 dark:bg-green-950/50 rounded-lg border border-green-200 dark:border-green-800">
              <p className="text-sm text-green-800 dark:text-green-200">
                <strong>Customer Self-Setup:</strong> The customer will configure DNS settings themselves using provided instructions.
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Domain Configuration */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Globe className="h-5 w-5" />
            Domain Configuration
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="root_domain">Root Domain</Label>
            <Input
              id="root_domain"
              value={formData.root_domain}
              onChange={(e) => handleFieldChange('root_domain', e.target.value)}
              placeholder="example.com"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="custom_sub_domain">Ordering Subdomain</Label>
              <div className="flex">
                <Input
                  id="custom_sub_domain"
                  value={formData.custom_sub_domain}
                  onChange={(e) => handleFieldChange('custom_sub_domain', e.target.value)}
                  placeholder="order"
                />
                <span className="flex items-center px-3 text-sm text-muted-foreground border border-l-0 rounded-r-md bg-muted">
                  .{formData.root_domain || 'domain.com'}
                </span>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="review_management_subdomain">Reviews Subdomain</Label>
              <div className="flex">
                <Input
                  id="review_management_subdomain"
                  value={formData.review_management_subdomain}
                  onChange={(e) => handleFieldChange('review_management_subdomain', e.target.value)}
                  placeholder="reviews"
                />
                <span className="flex items-center px-3 text-sm text-muted-foreground border border-l-0 rounded-r-md bg-muted">
                  .{formData.root_domain || 'domain.com'}
                </span>
              </div>
            </div>
          </div>

          <Button variant="outline" onClick={handleGenerateSubdomains}>
            Generate Default Subdomains
          </Button>
        </CardContent>
      </Card>

      {/* IP Configuration */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Server className="h-5 w-5" />
            IP Configuration
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="ordering_subdomain_ip">Ordering Subdomain IP</Label>
              <Input
                id="ordering_subdomain_ip"
                value={formData.ordering_subdomain_ip}
                onChange={(e) => handleFieldChange('ordering_subdomain_ip', e.target.value)}
                placeholder="203.0.113.1"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="review_management_ip">Reviews Subdomain IP</Label>
              <Input
                id="review_management_ip"
                value={formData.review_management_ip}
                onChange={(e) => handleFieldChange('review_management_ip', e.target.value)}
                placeholder="203.0.113.2"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="nameserver_1">Nameserver 1</Label>
              <Input
                id="nameserver_1"
                value={formData.nameserver_1}
                onChange={(e) => handleFieldChange('nameserver_1', e.target.value)}
                placeholder="ns1.example.com"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="nameserver_2">Nameserver 2</Label>
              <Input
                id="nameserver_2"
                value={formData.nameserver_2}
                onChange={(e) => handleFieldChange('nameserver_2', e.target.value)}
                placeholder="ns2.example.com"
              />
            </div>
          </div>

          {hasBasicDomainInfo && (
            <div className="p-4 bg-muted/50 rounded-lg">
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-medium">DNS Instructions</h4>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={handleCopyARecordsEmail}>
                    <Copy className="h-4 w-4 mr-2" />
                    Copy A Records Email
                  </Button>
                  <Button variant="outline" size="sm" onClick={handleCopyNameserverEmail}>
                    <Copy className="h-4 w-4 mr-2" />
                    Copy Nameserver Email
                  </Button>
                </div>
              </div>
              <div className="text-sm space-y-1">
                <div className="font-mono space-y-1">
                  <div><strong>A Records:</strong></div>
                  <div className="ml-2">{formData.custom_sub_domain}.{formData.root_domain} → {formData.ordering_subdomain_ip}</div>
                  <div className="ml-2">{formData.review_management_subdomain}.{formData.root_domain} → {formData.review_management_ip}</div>
                </div>
                <div className="font-mono space-y-1 mt-3">
                  <div><strong>Nameservers:</strong></div>
                  <div className="ml-2">{formData.nameserver_1}</div>
                  <div className="ml-2">{formData.nameserver_2}</div>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* DNS Provider Access (for Remote Setup) */}
      {formData.dns_setup_method === SetupMethod.REMOTE_ACCESS && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Settings className="h-5 w-5" />
              DNS Provider Access
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="dns_provider_name">DNS Provider</Label>
              <Select
                value={formData.dns_provider_name}
                onValueChange={(value) => handleFieldChange('dns_provider_name', value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select DNS provider" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="godaddy">Go Daddy</SelectItem>
                  <SelectItem value="freeparking">Free Parking</SelectItem>
                  <SelectItem value="namecheap">Namecheap</SelectItem>
                  <SelectItem value="crazydomains">Crazy Domains</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {formData.dns_provider_name === 'other' && (
              <div className="space-y-2">
                <Label htmlFor="dns_provider_custom_name">Custom Provider Name</Label>
                <Input
                  id="dns_provider_custom_name"
                  value={formData.dns_provider_custom_name}
                  onChange={(e) => handleFieldChange('dns_provider_custom_name', e.target.value)}
                  placeholder="Provider name"
                />
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="dns_provider_username">Username/Email</Label>
                <Input
                  id="dns_provider_username"
                  value={formData.dns_provider_username}
                  onChange={(e) => handleFieldChange('dns_provider_username', e.target.value)}
                  placeholder="username@example.com"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="dns_provider_password">Password</Label>
                <div className="relative">
                  <Input
                    id="dns_provider_password"
                    type={showPassword ? "text" : "password"}
                    value={formData.dns_provider_password}
                    onChange={(e) => handleFieldChange('dns_provider_password', e.target.value)}
                    placeholder="••••••••"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="absolute right-0 top-0 h-full px-3 hover:bg-transparent"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </Button>
                </div>
              </div>
            </div>

            <div className="p-4 bg-amber-50 dark:bg-amber-950/50 rounded-lg border border-amber-200 dark:border-amber-800">
              <p className="text-sm text-amber-800 dark:text-amber-200">
                <strong>Security Note:</strong> DNS provider credentials are stored securely and only used for authorized domain configuration.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Actions */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Actions</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap gap-2">
            <Button 
              onClick={handleSave} 
              disabled={!hasChanges || saving}
            >
              <Save className="h-4 w-4 mr-2" />
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
            
            {hasBasicDomainInfo && !isComplete && (
              <>
                <Button 
                  variant="default"
                  onClick={() => handleStatusUpdate(OnboardingStatus.AWAITING_CONFIRMATION)}
                >
                  Mark as Ready for Setup
                </Button>
                
                <Button 
                  variant="default"
                  onClick={() => handleStatusUpdate(OnboardingStatus.COMPLETE)}
                >
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Mark as Complete
                </Button>
              </>
            )}
            
            <Button 
              variant="outline"
              onClick={() => handleStatusUpdate(OnboardingStatus.NEEDS_ATTENTION)}
            >
              <AlertCircle className="h-4 w-4 mr-2" />
              Mark as Needs Attention
            </Button>
          </div>
          
          <div className="p-4 bg-muted/50 rounded-lg">
            <p className="text-sm text-muted-foreground">
              {!hasBasicDomainInfo 
                ? "Enter the root domain and configure subdomains to proceed with DNS setup."
                : formData.dns_setup_method === SetupMethod.REMOTE_ACCESS
                ? "With remote access setup, provide DNS provider credentials to complete the configuration."
                : "Provide DNS configuration details for the customer to implement themselves."
              }
            </p>
          </div>
        </CardContent>
      </Card>

      {/* DNS Instructions Dialog (for Manual DNS Mode) */}
      <Dialog open={showDnsInstructions} onOpenChange={setShowDnsInstructions}>
        <DialogContent className="max-w-[min(90vw,1400px)] max-h-[90vh] overflow-y-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              DNS Instructions for {formData.root_domain}
            </DialogTitle>
            <DialogDescription>
              Copy these records and provide to the customer to add at their DNS provider
            </DialogDescription>
          </DialogHeader>

          {dnsInstructions && (
            <div className="space-y-4">
              {/* Copy Button */}
              <Button
                onClick={() => {
                  if (dnsInstructions.formatted_text) {
                    navigator.clipboard.writeText(dnsInstructions.formatted_text);
                    toast({
                      title: "Copied!",
                      description: "DNS instructions copied to clipboard"
                    });
                  }
                }}
              >
                <Copy className="h-4 w-4 mr-2" />
                Copy All Instructions
              </Button>

              {/* Formatted Text */}
              <div>
                <Label>Copy-Paste Format (for email/chat)</Label>
                <pre className="p-4 bg-muted rounded-lg overflow-auto text-xs font-mono whitespace-pre-wrap mt-2 max-h-96 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
                  {dnsInstructions.formatted_text}
                </pre>
              </div>

              {/* Critical Warnings */}
              <Alert variant="destructive">
                <AlertTriangle className="h-4 w-4 stroke-white" />
                <AlertTitle className="text-white">Critical Requirements</AlertTitle>
                <AlertDescription className="space-y-1 [&>p]:text-white">
                  <p>• All CNAME records (DKIM) must be set to "DNS only" (NOT proxied)</p>
                  <p>• All A records must be set to "DNS only" (NOT proxied)</p>
                  <p>• Wait 24-48 hours for DNS propagation before verifying</p>
                </AlertDescription>
              </Alert>

              {/* Record Summary */}
              {dnsInstructions.dns_records && (
                <div className="p-4 bg-muted/50 rounded-lg">
                  <p className="font-medium mb-2">Record Summary:</p>
                  <div className="text-sm space-y-1">
                    <p>• Total Records: {dnsInstructions.dns_records.length}</p>
                    <p>• Required: {dnsInstructions.dns_records.filter((r: any) => r.required).length}</p>
                    <p>• Optional: {dnsInstructions.dns_records.filter((r: any) => !r.required).length}</p>
                  </div>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDnsInstructions(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DomainSetupTab;